import React, { useState, useEffect, useMemo, useReducer } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "../../App.jsx";
import { useQueries } from "@tanstack/react-query";
import {
  q_prizebox_txns,
  qiserr,
  qissuccesss,
} from "../../queries/queries.jsx";
import { cdelay, getv, nils } from "../../utils/utils.jsx";
import { useAuthContext } from "../../wrappers/AuthWrapper.jsx";
import { useLayoutContext } from "../../components/Layout.jsx";
import { Card, Tag, Img } from "../../components/utilityComps.jsx";
import { metamaskimg } from "../../utils/links.jsx";
import { twMerge } from "tailwind-merge";
import { Loader01c } from "../../components/anims.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import colors from "tailwindcss/colors";
import _ from "lodash";
import ERC1155base from "../../contracts/v2/ERC1155base.js";
import PrizeBoxOpener from "../../contracts/v2/PrizeBoxOpener.js";
import { contractAddress_list } from "../../contracts/contracts.jsx";
import { json_to_base64 } from "../../utils/utils.jsx";
import {
  boxtypemap,
  TransactionTable,
  PrizeboxContext,
  GlassCard,
  GradientCard,
  GlowButton,
  ModernLoader,
  TransactionTableSkeleton,
} from "./dependencies.jsx";
import { PrizeboxPopupOverlay } from "./PrizeboxPopupOverlay.jsx";
import { usePageContext } from "./PrizeboxPage.jsx";

// ClosedBox component with ERC1155 balance fetching for BigBox
const ClosedBoxUI = ({ boxtype, onOpenClick }) => {
  const aucon = useAuthContext();
  const { vault, auth } = aucon;
  const bmap = boxtypemap[boxtype];

  const [count_loading, set_count_loading] = useState(true);
  const [closed_n, set_closed_n] = useState(0);

  const ciparams = useMemo(() => {
    if (!aucon.auth || !aucon.aumode || !aucon.vault) return null;
    return aucon.get_contract_init_params();
  }, [aucon.aumode, aucon.vault, aucon.auth]);

  const update_closed = async () => {
    try {
      set_count_loading(true);
      const contract = await ERC1155base.get_contract("PRIZEBOX", ciparams);
      const balance = await contract.balanceOf(vault, boxtype);
      set_closed_n(Number(balance));
      set_count_loading(false);
    } catch (err) {
      console.log(err);
      set_count_loading(false);
      setTimeout(update_closed, 5 * 1e3);
    }
  };

  useEffect(() => {
    let interval = null;
    if (auth && vault && ciparams) {
      setTimeout(update_closed, 2 * 1e3);
      interval = setInterval(update_closed, 2 * 60 * 1e3);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [auth, vault, ciparams]);

  return (
    <div className="w-full">
      <GlassCard className="p-8 text-center w-full max-w-2xl mx-auto hover-lift">
        <h3 className="font-digi text-xl md:text-2xl text-r3lig mb-6">
          {bmap.name}
        </h3>

        {/* Box Image with Glow */}
        <div className="relative mb-6 group">
          <div className="relative w-48 h-48 md:w-64 md:h-64 mx-auto">
            {/* Gradient Glow Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-r3lig/30 via-r3reg/20 to-r3dark/30 rounded-3xl blur-2xl scale-110 animate-pulse" />

            {/* Box Image */}
            <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
              <Img
                img={bmap.img}
                className="w-full h-full object-contain rounded-2xl shadow-2xl"
              />
            </div>

            {/* Hover Glow Enhancement */}
            <div className="absolute inset-0 bg-gradient-to-br from-r3lig/20 to-r3reg/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl scale-125" />
          </div>
        </div>

        {/* Counter Display */}
        <div className="mb-8">
          {!auth ? (
            <div className="h-8"></div>
          ) : count_loading ? (
            <div className="flex justify-center">
              <ModernLoader size="md" />
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-4xl md:text-5xl font-digi font-bold text-r3lig animate-glow-pulse rounded-lg px-3 py-1">
                {closed_n}
              </p>
              <p className="text-gray-400 resp-text-0">
                {closed_n === 1 ? "Box Available" : "Boxes Available"}
              </p>
            </div>
          )}
        </div>

        {/* Action Button */}
        {closed_n > 0 ? (
          <div className="flex justify-center">
            <GlowButton
              onClick={onOpenClick}
              variant="primary"
              className="px-8 py-4 text-lg"
            >
              Open 1 Box
            </GlowButton>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="px-8 py-4 text-gray-500 font-digi text-lg">
              No boxes available
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

// ==================================================================================
// STEP CONFIGURATION
// ==================================================================================

const BIGBOX_STEPS = [
  {
    key: "selection",
    title: "Choose Your Option",
    description: "Select how you want to open your Big Box",
  },
  {
    key: "approval",
    title: "Contract Approval",
    description: "Approve the contract to burn your Big Box",
  },
  {
    key: "processing",
    title: "Opening Box",
    description: "Processing your Big Box transaction",
  },
];

// ==================================================================================
// PURE STEP COMPONENTS
// ==================================================================================

// Step 1: Option Selection
const SelectionStep = ({ onSelectOption, onClose }) => {
  return (
    <div className="space-y-6 p-6">
      <div className="text-center">
        <h2 className="text-r3lig font-digi resp-text-2 mb-2 animate-glow-pulse rounded-lg px-3 py-1">
          Choose Your Big Box Option
        </h2>
        <p className="text-gray-300 resp-text-0">
          Select how you want to open your Big Box
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Fixed Option */}
        <GradientCard
          onClick={() => onSelectOption("fixed")}
          className="p-8 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/30 border-2 border-transparent hover:border-green-400/50 group relative overflow-hidden"
        >
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="text-center relative z-10">
            <div className="text-7xl mb-6 transform group-hover:scale-110 transition-transform duration-300">💰</div>
            <h3 className="text-white font-digi resp-text-1 mb-3 group-hover:text-green-300 transition-colors duration-300">
              Take the Guaranteed Prize
            </h3>
            <p className="text-green-400 font-bold resp-text-2 mb-4 animate-glow-pulse rounded-md px-2 py-1">$200.00</p>
            <p className="text-gray-300 resp-text--1 group-hover:text-gray-200 transition-colors duration-300">
              Get the guaranteed cash prize instantly
            </p>
          </div>

          {/* Hover glow border */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-400/20 to-emerald-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
        </GradientCard>

        {/* Random Option */}
        <GradientCard
          onClick={() => onSelectOption("random")}
          className="p-8 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-r3reg/30 border-2 border-transparent hover:border-r3lig/50 group relative overflow-hidden"
        >
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-r3lig/10 to-r3reg/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          <div className="text-center relative z-10">
            <div className="text-7xl mb-6 transform group-hover:scale-110 transition-transform duration-300">📦</div>
            <h3 className="text-white font-digi resp-text-1 mb-3 group-hover:text-r3lig transition-colors duration-300">
              Open the Big Box
            </h3>
            <p className="text-r3lig font-bold resp-text-1 mb-4 animate-glow-pulse rounded-md px-2 py-1">
              It could be anything!
            </p>
            <p className="text-gray-300 resp-text--1 group-hover:text-gray-200 transition-colors duration-300">
              Take your chances with the mystery box
            </p>
          </div>

          {/* Hover glow border */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-r3lig/20 to-r3reg/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
        </GradientCard>
      </div>

      <div className="text-center">
        <GlowButton
          onClick={onClose}
          variant="secondary"
          className="px-8 py-3"
        >
          Cancel
        </GlowButton>
      </div>
    </div>
  );
};

// Step 2: Approval
const ApprovalStep = ({
  onApprove,
  isApproved,
  isLoading,
  isApproving,
  onClose,
}) => {
  return (
    <div className="space-y-6 p-6">
      <div className="text-center">
        <h2 className="text-r3lig font-digi resp-text-2 mb-2 animate-glow-pulse rounded-lg px-3 py-1">
          Big Box Approval
        </h2>
        <p className="text-gray-300 resp-text-0">
          The opener needs approval to burn your Big Box
        </p>
      </div>

      <div className="flex justify-center">
        <GlassCard className="p-8 max-w-md w-full">
          {isLoading ? (
            <div className="text-center space-y-4">
              <div className="relative">
                <div className="absolute inset-0 bg-r3lig/20 blur-3xl animate-pulse rounded-full" />
                <div className="relative z-10 flex justify-center">
                  <ModernLoader size="xl" />
                </div>
              </div>
              <p className="text-gray-300 resp-text-0 animate-pulse">
                Checking approval status...
              </p>
            </div>
          ) : isApproved ? (
            <div className="text-center space-y-4">
              <div className="relative">
                <div className="absolute inset-0 bg-green-500/30 blur-3xl animate-pulse rounded-full" />
                <div className="relative z-10 text-green-400 text-8xl animate-bounce">✓</div>
              </div>
              <h3 className="text-green-400 font-digi resp-text-1 animate-glow-pulse rounded-md px-2 py-1">
                Approved!
              </h3>
              <p className="text-gray-300 resp-text--1">
                Proceeding to next stage...
              </p>
            </div>
          ) : (
            <div className="text-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-500/20 blur-3xl animate-pulse rounded-full" />
                <div className="relative z-10 text-yellow-400 text-8xl animate-pulse">⚠️</div>
              </div>

              <div className="space-y-4">
                <h3 className="text-white font-digi resp-text-1">
                  Approval Required
                </h3>
                <p className="text-gray-300 resp-text--1">
                  The PrizeBox Opener contract needs permission to burn your Big
                  Box. This is a one-time approval.
                </p>
              </div>

              {isApproving ? (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <ModernLoader size="lg" />
                  </div>
                  <p className="text-r3lig resp-text-0 animate-pulse">
                    Waiting for approval transaction...
                  </p>
                </div>
              ) : (
                <GlowButton
                  onClick={onApprove}
                  variant="primary"
                  className="w-full py-4 text-lg"
                >
                  Approve Big Box Opener
                </GlowButton>
              )}
            </div>
          )}
        </GlassCard>
      </div>

      {!isApproved && !isApproving && (
        <div className="text-center">
          <GlowButton
            onClick={onClose}
            variant="secondary"
            className="px-8 py-3"
          >
            Cancel
          </GlowButton>
        </div>
      )}
    </div>
  );
};

// Step 3: Processing
const ProcessingStep = ({
  isProcessing,
  processingStatus,
  selectedMode,
  error,
  onRetry,
  onClose,
}) => {
  if (error) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex justify-center">
          <GlassCard className="p-8 max-w-md w-full">
            <div className="text-center space-y-6">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500/20 blur-3xl animate-pulse rounded-full" />
                <div className="relative z-10 text-red-400 text-8xl animate-pulse">❌</div>
              </div>

              <div className="space-y-4">
                <h2 className="text-red-400 font-digi resp-text-2 animate-glow-pulse rounded-lg px-3 py-1">
                  Transaction Failed
                </h2>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-300 resp-text-0 break-words">{error}</p>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <GlowButton
                  onClick={onRetry}
                  variant="danger"
                  className="flex-1"
                >
                  Try Again
                </GlowButton>
                <GlowButton
                  onClick={onClose}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </GlowButton>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-center">
        <GlassCard className="p-8 max-w-md w-full">
          <div className="text-center space-y-6">
            <div className="relative">
              <div className="absolute inset-0 bg-r3lig/20 blur-3xl animate-pulse rounded-full" />
              <div className="relative z-10 text-r3lig text-8xl animate-bounce">📦</div>
            </div>

            <div className="space-y-4">
              <h2 className="text-r3lig font-digi resp-text-2 animate-glow-pulse rounded-lg px-3 py-1">
                Opening Your Big Box
              </h2>
              <div className="bg-r3dark/20 border border-r3lig/30 rounded-xl p-4">
                <p className="text-gray-300 resp-text-0">
                  Selected mode: <span className="text-r3lig font-semibold">{selectedMode}</span>
                </p>
              </div>
            </div>

            {isProcessing ? (
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-r3reg/20 blur-2xl animate-pulse rounded-full" />
                  <div className="relative z-10 flex justify-center">
                    <ModernLoader size="xl" />
                  </div>
                </div>
                <div className="bg-r3reg/10 border border-r3reg/30 rounded-xl p-4">
                  <p className="text-r3lig resp-text-0 animate-pulse">
                    {processingStatus || "Processing your Big Box..."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-500/30 blur-3xl animate-pulse rounded-full" />
                  <div className="relative z-10 text-green-400 text-8xl animate-bounce">🎉</div>
                </div>
                <h3 className="text-green-400 font-digi resp-text-1 animate-glow-pulse rounded-md px-2 py-1">
                  Box Opened Successfully!
                </h3>
              </div>
            )}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

// ==================================================================================
// PROGRESS INDICATOR
// ==================================================================================

const ProgressIndicator = ({ steps, currentStepIndex }) => {
  return (
    <div className="flex items-center justify-center space-x-4 mb-6">
      {steps.map((step, index) => (
        <div key={step.key} className="flex items-center">
          <div
            className={twMerge(
              "w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 backdrop-blur-xl border",
              index < currentStepIndex
                ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/50 border-green-400/30"
                : index === currentStepIndex
                ? "bg-gradient-to-r from-r3reg to-r3dark text-white shadow-lg shadow-r3reg/50 animate-glow-pulse border-r3lig/30 rounded-lg"
                : "bg-r3dark/20 text-gray-400 border-gray-500/30"
            )}
          >
            {index < currentStepIndex ? "✓" : index + 1}
          </div>
          {index < steps.length - 1 && (
            <div
              className={twMerge(
                "w-16 h-1 mx-2 rounded-full transition-all duration-500",
                index < currentStepIndex
                  ? "bg-gradient-to-r from-green-500 to-green-400 shadow-sm shadow-green-500/30"
                  : "bg-gray-600/30"
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// ==================================================================================
// MAIN MULTI-STEP MODAL COMPONENT
// ==================================================================================

const BigBoxMultiStepModal = ({
  open,
  currentStepIndex,
  selectedMode,
  onClose,
  onSelectOption,
  onApprove,
  isApproved,
  approvalLoading,
  isApproving,
  isProcessing,
  processingStatus,
  processingError,
  onRetryProcessing,
}) => {
  const currentStep = BIGBOX_STEPS[currentStepIndex];

  const renderCurrentStep = () => {
    switch (currentStep?.key) {
      case "selection":
        return (
          <SelectionStep onSelectOption={onSelectOption} onClose={onClose} />
        );
      case "approval":
        return (
          <ApprovalStep
            onApprove={onApprove}
            isApproved={isApproved}
            isLoading={approvalLoading}
            isApproving={isApproving}
            onClose={onClose}
          />
        );
      case "processing":
        return (
          <ProcessingStep
            isProcessing={isProcessing}
            processingStatus={processingStatus}
            selectedMode={selectedMode}
            error={processingError}
            onRetry={onRetryProcessing}
            onClose={onClose}
          />
        );
      default:
        return null;
    }
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[200] backdrop-blur-md transition-all duration-500 animate-modal-entrance opacity-100 pointer-events-all"
    >
      <div className="bg-black/20 w-full h-full fc-cc p-4">
        <GlassCard className="max-w-4xl mx-auto w-full max-h-[90vh] overflow-y-auto hover-lift">
          {/* Header with Progress */}
          <div className="p-6 border-b border-r3lig/20">
            <h1 className="text-r3lig font-digi resp-text-3 text-center mb-4 animate-glow-pulse rounded-xl px-4 py-2">
              Big Box Opening
            </h1>
            <ProgressIndicator
              steps={BIGBOX_STEPS}
              currentStepIndex={currentStepIndex}
            />
            <p className="text-gray-300 text-center resp-text-0">
              {currentStep?.description}
            </p>
          </div>

          {/* Step Content */}
          <div className="min-h-[400px]">{renderCurrentStep()}</div>
        </GlassCard>
      </div>
    </div>
  );

  // Render modal in a portal (outside component tree for full viewport coverage)  
  return createPortal(modalContent, document.body);
};

// BigBox Section with UI-only component
const BigBoxSectionLocal = ({ onOpenClick }) => {
  return (
    <div className="fr-sc resp-gap-2">
      <ClosedBoxUI boxtype={2} onOpenClick={onOpenClick} />
    </div>
  );
};

// Side panel component for BigBox transactions
export const BigboxSidePanel = () => {
  const appcon = useAppContext();
  const { psearch } = appcon;

  const aucon = useAuthContext();
  const vault = useMemo(() => {
    if (!nils(psearch.v)) return psearch.v.toLowerCase();
    return aucon.vault;
  }, [psearch.v, aucon.vault]);

  const [qo_txns] = useQueries({
    queries: [q_prizebox_txns({ vault, boxtype: 2 }, { enabled: !nils(vault) })],
  });

  return (
    <div className="h-full pt-2">

      <div className="h-[80vh] md:h-[60vh] overflow-hidden">
        {qo_txns.isLoading ? (
          <TransactionTableSkeleton />
        ) : qiserr(qo_txns) ? (
          <GlassCard className="p-6 h-full flex items-center justify-center">
            <p className="text-center text-red-300 resp-text--1">
              {qiserr(qo_txns)}
            </p>
          </GlassCard>
        ) : qissuccesss(qo_txns) &&
          _.isEmpty(getv(qo_txns, "data.result.txns")) ? (
          <GlassCard className="p-6 h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl mb-3 opacity-50">📦</div>
              <p className="text-yellow-300 resp-text--1">
                No transactions found
              </p>
            </div>
          </GlassCard>
        ) : qissuccesss(qo_txns) &&
          !_.isEmpty(getv(qo_txns, "data.result.txns")) ? (
          <div className="h-full">
            <PrizeboxContext.Provider value={{ qo_txns }}>
              <TransactionTable />
            </PrizeboxContext.Provider>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const BigboxTab = () => {
  const appcon = useAppContext();
  const { psearch, upd_psearch } = appcon;
  const tokenid = 2;
  const boxtype = tokenid;

  const aucon = useAuthContext();
  const { auth } = aucon;
  const vault = useMemo(() => {
    if (!nils(psearch.v)) return psearch.v.toLowerCase();
    return aucon.vault;
  }, [psearch.v, aucon.vault]);

  const laycon = useLayoutContext();
  const pagecon = usePageContext();
  const { tab } = pagecon;

  const [qo_txns] = useQueries({
    queries: [q_prizebox_txns({ vault, boxtype }, { enabled: !nils(vault) })],
  });

  const [showopen, set_showopen] = useState(false);
  const [box_error, set_box_error] = useState(null);

  // ==================================================================================
  // CENTRALIZED STATE MANAGEMENT
  // ==================================================================================

  // Multi-step modal state
  const [showModal, setShowModal] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedMode, setSelectedMode] = useState(null);

  // Approval state
  const [isApproved, setIsApproved] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingError, setProcessingError] = useState(null);

  useEffect(() => {
    if (!nils(psearch.boxopentx)) {
      set_showopen(true);
    }
  }, [psearch.boxopentx]);

  // ==================================================================================
  // CONTRACT HELPERS
  // ==================================================================================

  // Get contract init params
  const ciparams = useMemo(() => {
    if (!aucon.auth || !aucon.aumode || !aucon.vault) return null;
    return aucon.get_contract_init_params();
  }, [aucon.aumode, aucon.vault, aucon.auth]);

  // ==================================================================================
  // STEP HANDLERS
  // ==================================================================================

  // Handle opening the modal
  const handleBigBoxOpen = () => {
    setShowModal(true);
    setCurrentStepIndex(0);
    setSelectedMode(null);
    setIsApproved(false);
    setIsApproving(false);
    setApprovalLoading(false);
    setIsProcessing(false);
    setProcessingStatus(null);
    setProcessingError(null);
  };

  // Handle option selection
  const handleOptionSelect = async (mode) => {
    console.log("Option selected:", mode);
    setSelectedMode(mode);
    setCurrentStepIndex(1); // Move to approval step

    // Start checking approval status, passing the mode explicitly
    await cdelay(500);
    checkApprovalStatus(mode);
  };

  // Check if PrizeBoxOpener is approved
  const checkApprovalStatus = async (mode) => {
    try {
      setApprovalLoading(true);

      await cdelay(1000);
      const boxcon = await ERC1155base.get_contract("PRIZEBOX", ciparams);
      const approved = await boxcon.isApprovedForAll(
        vault,
        contractAddress_list.prizebox_opener,
      );

      setIsApproved(approved);
      setApprovalLoading(false);

      // If already approved, auto-proceed to processing
      if (approved) {
        await cdelay(2000);
        proceedToProcessing(mode);
      }
    } catch (err) {
      console.log("Error checking approval:", err);
      setApprovalLoading(false);
      setIsApproved(false);
    }
  };

  // Handle approval transaction
  const handleApproval = async () => {
    try {
      setIsApproving(true);

      const boxcon = await ERC1155base.get_contract("PRIZEBOX", ciparams);
      const tx = await boxcon.setApprovalForAll(
        contractAddress_list.prizebox_opener,
        true,
      );

      console.log("Approval transaction sent:", tx.hash);

      await cdelay(5000);

      setIsApproved(true);
      setIsApproving(false);

      await cdelay(2000);
      proceedToProcessing(selectedMode);
    } catch (err) {
      console.log("Error approving:", err);
      setIsApproving(false);
      set_box_error(err.message);
    }
  };

  // Proceed to processing stage
  const proceedToProcessing = async (mode = selectedMode) => {
    try {
      // Validate mode selection
      if (nils(mode)) {
        throw new Error("Selection mode not found");
      }

      setCurrentStepIndex(2); // Move to processing step
      setIsProcessing(true);
      setProcessingStatus("Preparing transaction...");

      console.log(`Burning BigBox with mode: ${mode}`);

      // Get PrizeBoxOpener contract
      const opencon = await PrizeBoxOpener.get_contract(ciparams);

      // Encode the mode selection
      let encoded = { mode: mode };
      encoded = json_to_base64(encoded);

      setProcessingStatus("Sending transaction...");
      upd_psearch({ boxopentx: "waiting_hash", bigbox_mode: mode });
      console.log(`Encoded data: ${encoded}`);

      // Send burn transaction
      const tx = await opencon.burnLootbox_wOptions(1, boxtype, encoded);

      console.log(`Burn transaction sent: ${tx.hash}`);
      setProcessingStatus("Transaction confirmed!");

      // Update search params with real transaction hash
      upd_psearch({ boxopentx: tx.hash, bigbox_mode: mode });

      setIsProcessing(false);

      // Close modal after success
      await cdelay(3000);
      handleModalClose();
    } catch (err) {
      console.error("Error burning BigBox:", err);
      setIsProcessing(false);
      setProcessingError(err.message);
      set_box_error(err.message);
    }
  };

  // Retry processing after error
  const handleRetryProcessing = () => {
    setProcessingError(null);
    proceedToProcessing(selectedMode);
  };

  // Handle modal close (resets everything)
  const handleModalClose = () => {
    setShowModal(false);
    setCurrentStepIndex(0);
    setSelectedMode(null);
    setIsApproved(false);
    setIsApproving(false);
    setApprovalLoading(false);
    setIsProcessing(false);
    setProcessingStatus(null);
    setProcessingError(null);
  };

  const pcon = { vault, qo_txns, set_showopen, box_error, set_box_error };

  return (
    <PrizeboxContext.Provider value={pcon}>
      <div className="">
        {/* Multi-Step BigBox Modal */}
        <BigBoxMultiStepModal
          open={showModal}
          currentStepIndex={currentStepIndex}
          selectedMode={selectedMode}
          onClose={handleModalClose}
          onSelectOption={handleOptionSelect}
          onApprove={handleApproval}
          isApproved={isApproved}
          approvalLoading={approvalLoading}
          isApproving={isApproving}
          isProcessing={isProcessing}
          processingStatus={processingStatus}
          processingError={processingError}
          onRetryProcessing={handleRetryProcessing}
        />

        {/* Standard Prizebox Popup Overlay - Results display */}
        {showopen && tab === "bigbox" ? <PrizeboxPopupOverlay pcon={pcon} /> : null}

        {/* Big Box Section - Full Width */}
        <div className="w-full pt-2">

          {auth !== true ? (
            <div className="flex justify-center">
              <GlassCard className="p-8 text-center max-w-md">
                <div className="space-y-6">
                  <h3 className="text-r3lig font-digi resp-text-1">
                    Please connect your wallet to continue
                  </h3>
                  <GlowButton
                    onClick={laycon.open_loginpop}
                    variant="primary"
                    className="flex items-center gap-3 px-6 py-3"
                  >
                    <div className="w-6 h-6">
                      <Img img={metamaskimg} />
                    </div>
                    <span>Connect Wallet</span>
                  </GlowButton>
                </div>
              </GlassCard>
            </div>
          ) : (
            <>
              <BigBoxSectionLocal onOpenClick={handleBigBoxOpen} />

              {/* Debug info - only show during development */}
              {selectedMode && process.env.NODE_ENV === 'development' && (
                <div className="mt-6">
                  <GlassCard className="p-4 max-w-md mx-auto">
                    <div className="text-center text-sm space-y-1">
                      <p className="text-r3lig">
                        Mode: <span className="text-green-400">{selectedMode}</span>
                      </p>
                      <p className="text-gray-400">
                        Step: {currentStepIndex + 1} of {BIGBOX_STEPS.length}
                      </p>
                      {currentStepIndex === 1 && (
                        <p className="text-r3reg">
                          Approval: {isApproved ? "✓ Approved" : isApproving ? "Approving..." : "Pending"}
                        </p>
                      )}
                    </div>
                  </GlassCard>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PrizeboxContext.Provider>
  );
};
